import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Entity } from '@/domain/document-processing/types/entity-types';
import { ConsolidatedEntity } from '@/types/consolidation-types';
import { EntityBadge } from '@/components/ui/entity-badge';
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  ChevronDown, 
  ChevronRight, 
  Users, 
  Building2, 
  User, 
  MapPin, 
  Phone, 
  Mail, 
  BookOpen, 
  FileText,
  Link as LinkIcon,
  Star,
  Info,
  Tag,
  Globe,
  Home,
  Briefcase,
  Building,
  Flag
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { EntityAuditModal } from '@/components/ui/entity-audit-modal';
import { EntityDetails } from '@/components/ui/entity-details';
import { getEntityMatchDetails } from '@/domain/document-processing/utils/entity-match-evidence';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardContent } from '@/components/ui/card';

// Screen-facing screening state. Blank names are marked approved without a
// screening call, so they count as not screened rather than as a clean result.
type ScreeningDisplay = 'match' | 'unscreened' | 'clear';
const screeningDisplay = (entity: { name?: string; reviewStatus?: string }): ScreeningDisplay =>
  entity.reviewStatus === 'flagged' ? 'match'
    : entity.reviewStatus === 'approved' && entity.name?.trim() ? 'clear'
    : 'unscreened';
const screeningOrder: Record<ScreeningDisplay, number> = { match: 0, unscreened: 1, clear: 2 };

export function EntityTable({ entities }: { entities: (Entity | ConsolidatedEntity | any)[] }) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const detailsId = React.useId();
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);

  const handleViewDetails = (entity: Entity) => {
    // Entity detail view opened - no logging needed for UI interaction
    setSelectedEntity(entity);
    setIsAuditModalOpen(true);
  };

  // Create a map of entity IDs to their actual entity object for relationship lookup
  const entityMap = entities.reduce((map, entity) => {
    map[entity.id] = entity;
    return map;
  }, {} as Record<string, Entity>);

  // Function to render relationship badges with tooltips
  const renderRelationshipBadge = (relType: string) => {
    const iconMap: Record<string, any> = {
      "supplier": <Users className="h-3 w-3" />,
      "customer": <Building2 className="h-3 w-3" />,
      "subsidiary": <BookOpen className="h-3 w-3" />,
      "parent": <Star className="h-3 w-3" />,
      "partner": <LinkIcon className="h-3 w-3" />,
      "employee": <User className="h-3 w-3" />,
      "affiliated": <Users className="h-3 w-3" />,
    };

    const icon = iconMap[relType.toLowerCase()] || <LinkIcon className="h-3 w-3" />;
    
    return (
      <Badge variant="outline" className="text-xs px-2 py-0 h-5 gap-1 border-slate-300">
        {icon}
        <span>{relType}</span>
      </Badge>
    );
  };
  
  // Get appropriate status icon based on review status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'flagged':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-amber-600" />;
    }
  };

  return (
    <>
      <div className="min-w-0" style={{ containerType: 'inline-size' }}>
      <Table className="party-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Party</TableHead>
            <TableHead>Details</TableHead>
            <TableHead>Source</TableHead>
            <TableHead className="w-[180px]">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...entities].sort((a, b) => screeningOrder[screeningDisplay(a)] - screeningOrder[screeningDisplay(b)]).map((sourceEntity) => {
            const entity = { ...sourceEntity, matchDetails: getEntityMatchDetails(sourceEntity) };
            const reviewStatus = entity.reviewStatus || 'pending review';
            const isExpanded = !collapsed.has(entity.id);
            const toggleExpanded = () => setCollapsed(previous => {
              const next = new Set(previous);
              if (next.has(entity.id)) next.delete(entity.id); else next.add(entity.id);
              return next;
            });
            const hasDetails = !!(reviewStatus === 'flagged' || entity.nameVariations?.length || entity.addresses?.length || entity.contacts?.length || entity.individuals?.length || entity.relationships?.length || entity.identifiers?.length || entity.context);
            const screening = screeningDisplay(entity);
            
            // Main row styling
            const mainRowClass = cn(
              'party-summary-row transition-colors',
              screening === 'clear'
                ? 'bg-green-50/70 hover:bg-green-50 border-l-2 border-l-green-500' 
                : reviewStatus === 'flagged' 
                  ? 'bg-muted/20 hover:bg-muted/40 border-l-2 border-l-destructive/40'
                  : 'hover:bg-slate-50 bg-transparent'
            );
            
            // Count enhanced entity data points
            const enhancedDataCount = [
              entity.addresses?.length || 0,
              entity.contacts?.length || 0,
              entity.relationships?.length || 0,
              entity.identifiers?.length || 0,
              entity.nameVariations?.length || 0,
              entity.individuals?.length || 0
            ].reduce((sum, count) => sum + count, 0);

            // Calculate status badge and icon
            let statusBadge;
            
            if (screening === 'clear') {
              statusBadge = (
                <Badge className="bg-green-50 text-green-700 border border-green-200 gap-1">
                  <CheckCircle2 className="h-4 w-4" />
                  No match above threshold
                </Badge>
              );
            } else if (reviewStatus === 'flagged') {
              statusBadge = (
                <Badge variant="outline" className="border-destructive/30 text-destructive gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Potential match
                </Badge>
              );
            } else {
              statusBadge = (
                <Badge className="bg-amber-50 text-amber-700 border border-amber-200 gap-1">
                  <Clock className="h-4 w-4" />
                  Not screened
                </Badge>
              );
            }
            
            return (
              <React.Fragment key={entity.id}>
                {/* Main entity row */}
                <TableRow 
                  className={cn(mainRowClass, hasDetails && 'cursor-pointer')}
                  onClick={event => {
                    if (hasDetails && !(event.target as HTMLElement).closest('button, a, input, summary')) toggleExpanded();
                  }}
                >
                  <TableCell className="py-3">
                    <div className="flex items-start gap-2">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <EntityBadge type={entity.type} iconOnly />
                          {hasDetails ? <button type="button" aria-expanded={isExpanded}
                            aria-controls={[
                              reviewStatus === 'flagged' && 'matches',
                              entity.nameVariations?.length && 'names', entity.addresses?.length && 'addresses',
                              entity.contacts?.length && 'contacts', entity.individuals?.length && 'people',
                              entity.relationships?.length && 'relationships', entity.identifiers?.length && 'identifiers', entity.context && 'context',
                            ].filter(Boolean).map(section => detailsId + entity.id + section).join(' ')}
                            onClick={toggleExpanded}
                            className="flex items-center gap-2 text-left font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                            <ChevronRight data-export-omit aria-hidden="true" className={cn('h-4 w-4 shrink-0 transition-transform', isExpanded && 'rotate-90')} />
                            {entity.name}
                          </button> : <span className="font-medium">{entity.name}</span>}
                        </div>
                        {entity.role && (
                          <Badge variant="outline" className="mt-1 text-xs w-fit">
                            <Briefcase className="mr-1 h-3 w-3" />
                            Role: {entity.role}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex flex-wrap gap-2">
                        {enhancedDataCount > 0 ? (
                          <>
                            {(entity.addresses?.length || 0) > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge variant="outline" className="text-primary gap-1 border-slate-200">
                                      <MapPin className="h-3 w-3" />
                                      {entity.addresses?.length} {entity.addresses?.length === 1 ? 'Address' : 'Addresses'}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{entity.addresses?.length} address{entity.addresses?.length !== 1 ? 'es' : ''}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            
                            {(entity.contacts?.length || 0) > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge variant="outline" className="text-primary gap-1 border-slate-200">
                                      <User className="h-3 w-3" />
                                      {entity.contacts?.length} {entity.contacts?.length === 1 ? 'Contact' : 'Contacts'}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{entity.contacts?.length} contact{entity.contacts?.length !== 1 ? 's' : ''}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            
                            {/* Add badge for individuals */}
                            {(entity.individuals?.length || 0) > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge variant="outline" className="text-primary gap-1 border-slate-200">
                                      <Users className="h-3 w-3" />
                                      {entity.individuals?.length} {entity.individuals?.length === 1 ? 'Individual' : 'Individuals'}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{entity.individuals?.length} individual{entity.individuals?.length !== 1 ? 's' : ''}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            
                            {(entity.relationships?.length || 0) > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge variant="outline" className="text-primary gap-1 border-slate-200">
                                      <LinkIcon className="h-3 w-3" />
                                      {entity.relationships?.length} {entity.relationships?.length === 1 ? 'Relationship' : 'Relationships'}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{entity.relationships?.length} relationship{entity.relationships?.length !== 1 ? 's' : ''}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            
                            {(entity.identifiers?.length || 0) > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge variant="outline" className="text-primary gap-1 border-slate-200">
                                      <BookOpen className="h-3 w-3" />
                                      {entity.identifiers?.length} {entity.identifiers?.length === 1 ? 'Identifier' : 'Identifiers'}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{entity.identifiers?.length} identifier{entity.identifiers?.length !== 1 ? 's' : ''}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            
                            {(entity.nameVariations?.length || 0) > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge variant="outline" className="text-primary gap-1 border-slate-200">
                                      <Tag className="h-3 w-3" />
                                      {entity.nameVariations?.length} {entity.nameVariations?.length === 1 ? 'Variation' : 'Variations'}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{entity.nameVariations?.length} name variation{entity.nameVariations?.length !== 1 ? 's' : ''}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground text-xs">Basic data only</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm flex items-center gap-1">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span>{entity.sourceDocument || 'Unknown'}</span>
                      {entity.pageNumber && (
                        <span className="text-muted-foreground">(p.{entity.pageNumber})</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label={`View details for ${entity.name}`}
                            aria-haspopup="dialog"
                            className="flex items-center gap-2 justify-between hover:bg-muted rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() => handleViewDetails(entity)}
                          >
                            {statusBadge}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View party details</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
                
                {/* The header retains screening status; the whole entity body collapses together. */}
                <>
                  {/* Keep matches in the table captured by PDF export, not only in the audit dialog. */}
                  {reviewStatus === 'flagged' && (
                    <TableRow data-party-detail id={detailsId + entity.id + 'matches'} hidden={!isExpanded} className="bg-background hover:bg-background">
                      <TableCell colSpan={4} className="px-4 pb-3 pt-0">
                        <EntityDetails entity={entity} />
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Name Variations Section */}
                  {entity.nameVariations && entity.nameVariations.length > 0 && (
                    <TableRow data-party-detail id={detailsId + entity.id + 'names'} hidden={!isExpanded} className="border-l-4 border-l-slate-200 bg-primary/[0.02]">
                      <TableCell colSpan={4} className="p-0">
                        <div className="p-3 pl-8">
                          <div className="mb-2 flex items-center">
                            <Tag className="h-4 w-4 text-primary mr-2" />
                            <span className="font-medium text-sm">Name Variations</span>
                          </div>
                          <div className="flex flex-wrap gap-2 ml-6">
                            {entity.nameVariations.map((variation, idx) => (
                              <Badge key={idx} variant="outline" className="text-slate-700">
                                {variation}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Address Section */}
                  {entity.addresses && entity.addresses.length > 0 && (
                    <TableRow data-party-detail id={detailsId + entity.id + 'addresses'} hidden={!isExpanded} className="border-l-4 border-l-slate-200 bg-primary/[0.02]">
                      <TableCell colSpan={4} className="p-0">
                        <div className="p-3 pl-8">
                          <div className="mb-2 flex items-center">
                            <MapPin className="h-4 w-4 text-primary mr-2" />
                            <span className="font-medium text-sm">Addresses</span>
                          </div>
                          <div className="space-y-2 ml-6">
                            {entity.addresses.map((address, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                {address.type ? (
                                  <Badge variant="outline" className="text-xs text-primary">
                                    {address.type}
                                  </Badge>
                                ) : (
                                  <Home className="h-4 w-4 text-primary" />
                                )}
                                <div className="text-sm">
                                  <div>{typeof address.full === 'string' ? address.full : JSON.stringify(address.full)}</div>
                                  {address.components && (
                                    <div className="text-xs text-muted-foreground mt-1">
                                      {address.components.city && (
                                        <span className="mr-1">
                                          <Globe className="h-3 w-3 inline-block mr-1" />
                                          {address.components.city}
                                        </span>
                                      )}
                                      {address.components.country && (
                                        <span>
                                          <Flag className="h-3 w-3 inline-block mr-1" />
                                          {address.components.country}
                                      </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Contacts Section */}
                  {entity.contacts && entity.contacts.length > 0 && (
                    <TableRow data-party-detail id={detailsId + entity.id + 'contacts'} hidden={!isExpanded} className="border-l-4 border-l-slate-200 bg-primary/[0.02]">
                      <TableCell colSpan={4} className="p-0">
                        <div className="p-3 pl-8">
                          <div className="mb-2 flex items-center">
                            <User className="h-4 w-4 text-primary mr-2" />
                            <span className="font-medium text-sm">Contacts</span>
                          </div>
                          <div className="space-y-3 ml-6">
                            {entity.contacts.map((contact, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <div className="text-sm">
                                  <div className="font-medium">{contact.name || contact.role || 'Contact'}</div>
                                  <div className="flex flex-wrap gap-3 mt-1.5">
                                    {contact.role && (
                                      <span className="text-xs flex items-center text-muted-foreground">
                                        <Briefcase className="h-3 w-3 mr-1.5 text-primary/70" />
                                        {contact.role}
                                      </span>
                                    )}
                                    {contact.email && (
                                      <span className="text-xs flex items-center text-muted-foreground">
                                        <Mail className="h-3 w-3 mr-1.5 text-primary/70" />
                                        {contact.email}
                                      </span>
                                    )}
                                    {contact.phone && (
                                      <span className="text-xs flex items-center text-muted-foreground">
                                        <Phone className="h-3 w-3 mr-1.5 text-primary/70" />
                                        {contact.phone}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Related Individuals Section (for consolidated entities) */}
                  {entity.individuals && entity.individuals.length > 0 && (
                    <TableRow data-party-detail id={detailsId + entity.id + 'people'} hidden={!isExpanded} className="border-l-4 border-l-slate-200 bg-primary/[0.02]">
                      <TableCell colSpan={4} className="p-0">
                        <div className="p-3 pl-8">
                          <div className="mb-2 flex items-center">
                            <Users className="h-4 w-4 text-primary mr-2" />
                            <span className="font-medium text-sm">Individuals</span>
                          </div>
                          <div className="space-y-3 ml-6">
                            {entity.individuals.map((individual, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <div className="text-sm">
                                  <div className="font-medium">{individual.name}</div>
                                  <div className="flex flex-wrap gap-3 mt-1.5">
                                    {individual.role && (
                                      <span className="text-xs flex items-center text-muted-foreground">
                                        <Briefcase className="h-3 w-3 mr-1.5 text-primary/70" />
                                        {individual.role}
                                      </span>
                                    )}
                                    {individual.contactDetails?.email && (
                                      <span className="text-xs flex items-center text-muted-foreground">
                                        <Mail className="h-3 w-3 mr-1.5 text-primary/70" />
                                        {individual.contactDetails.email}
                                      </span>
                                    )}
                                    {individual.contactDetails?.phone && (
                                      <span className="text-xs flex items-center text-muted-foreground">
                                        <Phone className="h-3 w-3 mr-1.5 text-primary/70" />
                                        {individual.contactDetails.phone}
                                      </span>
                                    )}
                                    {individual.confidence && (
                                      <span className="text-xs flex items-center text-muted-foreground">
                                        <Info className="h-3 w-3 mr-1.5 text-primary/70" />
                                        Confidence: {(individual.confidence * 100).toFixed(0)}%
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Relationships Section */}
                  {entity.relationships && entity.relationships.length > 0 && (
                    <TableRow data-party-detail id={detailsId + entity.id + 'relationships'} hidden={!isExpanded} className="border-l-4 border-l-slate-200 bg-primary/[0.02]">
                      <TableCell colSpan={4} className="p-0">
                        <div className="p-3 pl-8">
                          <div className="mb-2 flex items-center">
                            <LinkIcon className="h-4 w-4 text-primary mr-2" />
                            <span className="font-medium text-sm">Relationships</span>
                          </div>
                          <div className="space-y-2 ml-6">
                            {entity.relationships.map((rel, idx) => {
                              const targetEntity = entityMap[rel.targetEntityId];
                              return (
                                <div key={idx} className="flex items-start gap-2">
                                  {renderRelationshipBadge(rel.relationship)}
                                  <div className="text-sm">
                                    <div className="font-medium flex items-center gap-1">
                                      {targetEntity ? (
                                        <>
                                          {targetEntity.type === 'individual' ? (
                                            <User className="h-3 w-3 text-primary" />
                                          ) : targetEntity.type === 'organization' ? (
                                            <Building className="h-3 w-3 text-primary" />
                                          ) : (
                                            <Info className="h-3 w-3 text-primary" />
                                          )}
                                          {targetEntity.name}
                                        </>
                                      ) : (
                                        <span className="text-muted-foreground">Unknown Entity ({rel.targetEntityId})</span>
                                      )}
                                    </div>
                                    {rel.context && (
                                      <p className="text-xs text-muted-foreground mt-1">{rel.context}</p>
                                    )}
                                    <div className="text-xs mt-1">
                                      <span className="text-muted-foreground">Confidence: </span>
                                      <span className="font-medium">{(rel.confidence * 100).toFixed(0)}%</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Identifiers Section */}
                  {entity.identifiers && entity.identifiers.length > 0 && (
                    <TableRow data-party-detail id={detailsId + entity.id + 'identifiers'} hidden={!isExpanded} className="border-l-4 border-l-slate-200 bg-primary/[0.02]">
                      <TableCell colSpan={4} className="p-0">
                        <div className="p-3 pl-8">
                          <div className="mb-2 flex items-center">
                            <BookOpen className="h-4 w-4 text-primary mr-2" />
                            <span className="font-medium text-sm">Identifiers</span>
                          </div>
                          <div className="flex flex-wrap gap-2 ml-6">
                            {entity.identifiers.map((identifier, idx) => (
                              <Badge key={idx} variant="outline" className="flex items-center gap-2 p-2 text-sm font-normal">
                                <span className="text-xs bg-slate-100 rounded px-1 py-0.5 text-slate-800">{identifier.type}</span>
                                {identifier.value}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Context Section if available */}
                  {entity.context && (
                    <TableRow data-party-detail id={detailsId + entity.id + 'context'} hidden={!isExpanded} className="border-l-4 border-l-slate-200 bg-primary/[0.02]">
                      <TableCell colSpan={4} className="p-0">
                        <div className="p-3 pl-8">
                          <div className="mb-2 flex items-center">
                            <Info className="h-4 w-4 text-primary mr-2" />
                            <span className="font-medium text-sm">Context</span>
                          </div>
                          <div className="ml-6">
                            <p className="text-sm text-muted-foreground italic">{entity.context}</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      </div>

      {isAuditModalOpen && selectedEntity && (
        <EntityAuditModal 
          entity={selectedEntity} 
          isOpen={isAuditModalOpen} 
          onOpenChange={(open) => setIsAuditModalOpen(open)} 
        />
      )}
    </>
  );
}
